import React, { useState, useEffect } from 'react';
import { StatusBar } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { AppUpdate } from '@capawesome/capacitor-app-update'; // [✅ นำเข้า Plugin สำหรับเช็คอัปเดต]
import Swal from 'sweetalert2'; 
import api from './utils/api';

// Import components
import Navigation from './components/Navigation';
import FaceScanner from './components/FaceScanner';
import Login from './components/Login';
import MyReports from './components/MyReports';
import AdminDashboard from './components/AdminDashboard';
import EmployeeManagement from './components/EmployeeManagement';
import DailyReport from './components/DailyReport';
import LateReport from './components/LateReport';
import LeaveReport from './components/LeaveReport';
import SummaryReport from './components/SummaryReport';
import OrganizationSettings from './components/OrganizationSettings';
import MyProfile from './components/MyProfile';
import HolidayManagement from './components/HolidayManagement';
import LeaveRequestForm from './components/LeaveRequestForm'; 
import LeaveApproval from './components/LeaveApproval';
import LateRequestForm from './components/LateRequestForm';
import ForgotScanRequestForm from './components/ForgotScanRequestForm';
import LateApproval from './components/LateApproval'; 
import ForgotScanApproval from './components/ForgotScanApproval'; 
import DepartmentManagement from './components/DepartmentManagement'; 
import PermissionLeaveForm from './components/PermissionLeaveForm';
import Logs from './components/Logs';
import PermissionReport from './components/PermissionReport';
import OfficialBusinessManagement from './components/OfficialBusinessManagement';
import OfficialBusinessReport from './components/OfficialBusinessReport';
import AttendanceManager from './components/AttendanceManager';
import ExtraWorkDaysManagement from './components/ExtraWorkDaysManagement';
import SuperAdminDashboard from './components/SuperAdminDashboard'; 

import { Camera } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';

const NoInternetScreen = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 text-white">
    <div className="text-center p-8">
      <div className="mb-4 flex justify-center">
        <svg className="w-20 h-20 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold mb-2">ไม่พบสัญญาณอินเทอร์เน็ต</h2>
      <p className="text-gray-400">กรุณาตรวจสอบการเชื่อมต่อ Wi-Fi หรือข้อมูลมือถือของคุณ</p>
    </div>
  </div>
);

const App = () => {
  const [currentPage, setCurrentPage] = useState('login');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [appReady, setAppReady] = useState(false);
  const [user, setUser] = useState(null);
  
  // State สำหรับจัดการสถานะการอัปเดต เพื่อป้องกัน Modal ชนกัน
  const [appUpdateStatus, setAppUpdateStatus] = useState('checking'); // 'checking', 'required', 'ok', 'dismissed'

  // ตรวจสอบการเปิดผ่าน LINE Browser
  useEffect(() => {
    const checkLineBrowser = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      if (userAgent.indexOf("Line") > -1 || userAgent.indexOf("LINE") > -1) {
        Swal.fire({
          title: 'ไม่รองรับการใช้งานผ่าน LINE',
          text: 'กรุณากดจุด 3 จุดมุมล่างขวา แล้วเลือก "เปิดในเบราว์เซอร์เริ่มต้น"',
          icon: 'warning',
          allowOutsideClick: false,
          showConfirmButton: false,
          customClass: { popup: 'rounded-2xl' }
        });
      }
    };
    checkLineBrowser();
  }, []);

  // [✅ ตรวจสอบเวอร์ชันแอปจาก Google Play Store โดยตรง]
  useEffect(() => {
    const checkAppVersion = async () => {
      // 🛑 ถ้าไม่ได้รันบนแอปมือถือ ให้ผ่านสถานะเป็น ok ทันที
      if (!Capacitor.isNativePlatform()) {
        setAppUpdateStatus('ok');
        return;
      }

      try {
        if (Capacitor.getPlatform() === 'android') {
          // 1. ดึงสถานะการอัปเดตจาก Google Play Store
          const result = await AppUpdate.getAppUpdateInfo();
          
          // 2. updateAvailability === 2 หมายถึง "มีอัปเดตใหม่บน Play Store"
          if (result.updateAvailability === 2) {
            setAppUpdateStatus('required');
            
            Swal.fire({
              title: 'มีเวอร์ชันใหม่พร้อมใช้งาน',
              html: `
                <div class="mt-2 text-sm text-gray-600 leading-relaxed text-left">
                  ระบบพบเวอร์ชันใหม่บน Play Store<br><br>
                  เพื่อประสิทธิภาพการทำงานที่สมบูรณ์ ความปลอดภัยสูงสุด และประสบการณ์การใช้งานที่ดียิ่งขึ้น กรุณาอัปเดตแอปพลิเคชันของคุณ
                </div>
              `,
              icon: 'info',
              confirmButtonText: 'อัปเดตทันที',
              showCancelButton: true,
              cancelButtonText: 'ข้ามไปก่อน', 
              allowOutsideClick: false, 
              allowEscapeKey: false,    
              buttonsStyling: false,    
              customClass: { 
                popup: 'rounded-3xl p-6 shadow-2xl border border-gray-100', 
                title: 'text-xl font-bold text-gray-900',
                htmlContainer: 'm-0 p-0', 
                confirmButton: 'w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-xl shadow-md transition-all duration-200',
                cancelButton: 'w-full mt-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3.5 px-4 rounded-xl transition-all duration-200' 
              }
            }).then(async (resultAlert) => {
              if (resultAlert.isConfirmed) {
                // ให้ระบบเปิดหน้าต่างอัปเดตของ Play Store ขึ้นมาทันที
                await AppUpdate.performImmediateUpdate();
              } else {
                setAppUpdateStatus('dismissed');
              }
            });
          } else {
            // ไม่มีอัปเดต หรืออัปเดตเสร็จแล้ว
            setAppUpdateStatus('ok');
          }
        } else {
           // กรณีเป็น iOS หรือแพลตฟอร์มอื่น
           setAppUpdateStatus('ok');
        }
      } catch (error) {
        console.error('Error checking app version via Play Store:', error);
        setAppUpdateStatus('ok'); // ถ้าตรวจสอบล้มเหลว ให้ปล่อยผ่านเพื่อให้แอปทำงานต่อได้
      }
    };

    checkAppVersion();
  }, []);

  useEffect(() => {
    const setStatusBar = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await StatusBar.setStyle({ style: 'DARK' }); 
          await StatusBar.setBackgroundColor({ color: '#1F2937' });
        } catch (err) {
          console.log('StatusBar error:', err);
        }
      }
    };
    setStatusBar();
  }, []);

  useEffect(() => {
    const checkPermissions = async () => {
      if (!Capacitor.isNativePlatform()) return;

      try {
        const cameraStatus = await Camera.checkPermissions();
        if (cameraStatus.camera !== 'granted') {
           await Camera.requestPermissions();
        }
        const geoStatus = await Geolocation.checkPermissions();
        if (geoStatus.location !== 'granted') {
           await Geolocation.requestPermissions();
        }
      } catch (error) {
        console.error("Error requesting permissions:", error);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    setAppReady(true);
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        if (parsedUser.is_super_admin) setCurrentPage('super-admin-dashboard');
        else if (parsedUser.is_admin || parsedUser.is_staff) setCurrentPage('admin-dashboard');
        else setCurrentPage('face-scanner');
      } catch (error) {
        localStorage.clear();
        setCurrentPage('login');
      }
    }
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const initPushNotifications = async () => {
        try {
          if (Capacitor.getPlatform() === 'android') {
            await PushNotifications.createChannel({
              id: 'high_priority_channel',
              name: 'การแจ้งเตือนด่วน',
              importance: 5,
              visibility: 1,
              vibration: true,
            });
          }
          const permStatus = await PushNotifications.requestPermissions();
          if (permStatus.receive === 'granted') await PushNotifications.register();

          PushNotifications.addListener('registration', async (token) => {
            const userData = localStorage.getItem('user_data');
            if (userData) {
              const u = JSON.parse(userData);
              await api.savePushSubscription(token.value, u.employee_id);
            }
          });
        } catch (error) {
          console.error('Push Notifications Error:', error);
        }
      };
      initPushNotifications();
    }
  }, []);

  const logout = () => {
    localStorage.clear();
    setUser(null);
    setCurrentPage('login');
    setShowMobileMenu(false);
  };

  if (!appReady) return <div className="min-h-screen bg-blue-500 flex items-center justify-center text-white">กำลังเริ่มต้นระบบ...</div>;
  if (!isOnline) return <NoInternetScreen />;

  const isStaffOrAdmin = user?.is_admin || user?.is_staff;
  const noAccessComponent = (
    <div className="text-center py-12">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">ไม่มีสิทธิ์เข้าถึง</h2>
      <button onClick={() => setCurrentPage('login')} className="text-blue-500 underline">กลับหน้าล็อกอิน</button>
    </div>
  );

  const renderCurrentPage = () => {
    if (!user && currentPage !== 'login') {
       return <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
    }

    switch (currentPage) {
      case 'login': return user ? <FaceScanner user={user} appUpdateStatus={appUpdateStatus} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'face-scanner': return user ? <FaceScanner user={user} appUpdateStatus={appUpdateStatus} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'my-reports': return user ? <MyReports user={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'my-profile': return user ? <MyProfile user={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'leave-request': return user ? <LeaveRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'late-request': return user ? <LateRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'forgot-scan-request': return user ? <ForgotScanRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      case 'permission-leave': return user ? <PermissionLeaveForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      
      case 'super-admin-dashboard': return user?.is_super_admin ? <SuperAdminDashboard user={user} /> : noAccessComponent;
      case 'admin-dashboard': return isStaffOrAdmin ? <AdminDashboard setCurrentPage={setCurrentPage} user={user} /> : noAccessComponent;
      
      case 'employees': return isStaffOrAdmin ? <EmployeeManagement user={user} /> : noAccessComponent;
      case 'settings': return isStaffOrAdmin ? <OrganizationSettings /> : noAccessComponent;
      case 'attendance-manager': return isStaffOrAdmin ? <AttendanceManager user={user} /> : noAccessComponent;
      case 'logs': return isStaffOrAdmin ? <Logs /> : noAccessComponent;
      
      case 'daily-report': return isStaffOrAdmin ? <DailyReport user={user} /> : noAccessComponent;
      case 'summary-report': return isStaffOrAdmin ? <SummaryReport user={user} /> : noAccessComponent;
      case 'late-report': return isStaffOrAdmin ? <LateReport user={user} /> : noAccessComponent;
      case 'leave-report': return isStaffOrAdmin ? <LeaveReport user={user} /> : noAccessComponent;
      case 'official-business-report': return isStaffOrAdmin ? <OfficialBusinessReport user={user} /> : noAccessComponent;
      case 'permission-report': return isStaffOrAdmin ? <PermissionReport user={user} /> : noAccessComponent;

      case 'leave-approval': return isStaffOrAdmin ? <LeaveApproval user={user} /> : noAccessComponent;
      case 'official-business': return isStaffOrAdmin ? <OfficialBusinessManagement currentUser={user} /> : noAccessComponent;
      case 'late-approval': return isStaffOrAdmin ? <LateApproval user={user} /> : noAccessComponent;
      case 'forgot-scan-approval': return isStaffOrAdmin ? <ForgotScanApproval user={user} /> : noAccessComponent;
      case 'department-management': return isStaffOrAdmin ? <DepartmentManagement user={user} /> : noAccessComponent;
      case 'holidays': return isStaffOrAdmin ? <HolidayManagement user={user} /> : noAccessComponent;
      case 'extra-work-days': return isStaffOrAdmin ? <ExtraWorkDaysManagement user={user} /> : noAccessComponent;
      
      default:
        if (!user) return <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
        if (user?.is_super_admin) return <SuperAdminDashboard user={user} />;
        if (isStaffOrAdmin) return <AdminDashboard setCurrentPage={setCurrentPage} user={user} />;
        return <FaceScanner user={user} appUpdateStatus={appUpdateStatus} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative pb-8 pb-[env(safe-area-inset-bottom)]"> 
      <Navigation 
        user={user} 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        logout={logout}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
      />
      <main className="container mx-auto px-4 py-8 mt-20 pt-[env(safe-area-inset-top)]">
        {renderCurrentPage()}
      </main>
    </div>
  );
};

export default App;